import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, ArrowRight, Loader2, Sparkles } from "lucide-react";
import {
  INITIAL_FORM,
  MISSING_NEEDS,
  PARTNER_OPTIONS,
  COST_OPTIONS,
  PROPOSAL_TYPES,
  STEPS,
} from "@/lib/formSchema";
import { WIZARD } from "@/constants/testIds";
import { SITE } from "@/config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Field({ label, name, value, onChange, type = "text", placeholder, required }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={name} className="text-sm font-medium text-slate-700">
        {label} {required && <span className="text-rose-500">*</span>}
      </Label>
      <Input
        id={name}
        type={type}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        placeholder={placeholder}
        className="rounded-xl border-slate-200 bg-white px-4 py-6"
        data-testid={WIZARD.field(name)}
      />
    </div>
  );
}

function Area({ label, name, value, onChange, placeholder, rows = 3 }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={name} className="text-sm font-medium text-slate-700">
        {label}
      </Label>
      <Textarea
        id={name}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="rounded-xl border-slate-200 bg-white px-4 py-3 leading-relaxed"
        data-testid={WIZARD.field(name)}
      />
    </div>
  );
}

function CheckGroup({ name, options, selected, onToggle }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {options.map((opt) => {
        const checked = selected.includes(opt);
        return (
          <label
            key={opt}
            className={`flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${
              checked
                ? "border-primary bg-teal-50/60"
                : "border-slate-200 bg-white hover:bg-slate-50"
            }`}
          >
            <Checkbox
              checked={checked}
              onCheckedChange={() => onToggle(name, opt)}
              data-testid={WIZARD.checkbox(name, opt.replace(/\s+/g, "-").toLowerCase())}
            />
            <span className="text-sm text-slate-800">{opt}</span>
          </label>
        );
      })}
    </div>
  );
}

export default function Wizard() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));
  const toggleArr = (name, value) =>
    setForm((f) => ({
      ...f,
      [name]: f[name].includes(value)
        ? f[name].filter((v) => v !== value)
        : [...f[name], value],
    }));

  const progress = useMemo(() => Math.round(((step - 1) / STEPS.length) * 100), [step, STEPS]);
  const current = STEPS[step - 1];

  const validateStep = () => {
    if (step === 1) {
      const need = ["organization_name", "contact_name", "email", "program_name"];
      const missing = need.find((k) => !form[k].trim());
      if (missing) {
        toast.error("Please fill in all required fields before continuing.");
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    if (step < STEPS.length) setStep(step + 1);
  };
  const back = () => step > 1 && setStep(step - 1);

  const submit = async () => {
    if (!validateStep()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/proposals/generate`, { form });
      sessionStorage.setItem(
        "fl_proposal",
        JSON.stringify({ ...data, form })
      );
      toast.success("Your proposal draft is ready!");
      navigate("/output");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Could not generate proposal. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={WIZARD.root} className="min-h-screen">
      {/* Top bar */}
      <header className="nav-bar">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-700 hover:text-slate-900"
            data-testid="wizard-home-link"
          >
            <Sparkles className="h-5 w-5 text-primary" />
            <span className="font-heading font-semibold">{SITE.brand}</span>
          </button>
          <span
            className="text-xs font-medium text-slate-500"
            data-testid={WIZARD.stepLabel}
          >
            Step {step} of {STEPS.length} — {current.label}
          </span>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {/* Progress */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-primary">
              {current.label}
            </span>
            <span className="text-xs text-slate-500">{progress}% complete</span>
          </div>
          <Progress
            value={progress}
            className="h-2 bg-slate-100"
            data-testid={WIZARD.progress}
          />
        </div>

        <div className="paper-surface p-6 sm:p-10 fade-in-up" key={step}>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight">
            {current.title}
          </h1>
          <p className="mt-2 text-slate-600 text-sm sm:text-base">{current.subtitle}</p>

          <div className="mt-8 space-y-5">
            {step === 1 && (
              <>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Organization Name" name="organization_name" value={form.organization_name} onChange={setField} required />
                  <Field label="Contact Name" name="contact_name" value={form.contact_name} onChange={setField} required />
                  <Field label="Email Address" name="email" value={form.email} onChange={setField} type="email" required />
                  <Field label="Program or Project Name" name="program_name" value={form.program_name} onChange={setField} required />
                  <Field label="City and State" name="city_state" value={form.city_state} onChange={setField} placeholder="e.g. Tulsa, OK" />
                  <Field label="Amount of Funding Requested" name="funding_amount" value={form.funding_amount} onChange={setField} placeholder="$25,000" />
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <p className="text-sm text-slate-600 -mt-2">
                  Who is the person, family, group, school, neighborhood, or community you want to help?
                </p>
                <Area label="Target population" name="target_population" value={form.target_population} onChange={setField} placeholder="e.g. middle school youth in North Tulsa" />
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Age range (if applicable)" name="age_range" value={form.age_range} onChange={setField} placeholder="11–14" />
                  <Field label="Location served" name="location_served" value={form.location_served} onChange={setField} placeholder="ZIP 74106 and 74110" />
                </div>
                <Area label="Why this group matters to you" name="why_group_matters" value={form.why_group_matters} onChange={setField} />
              </>
            )}

            {step === 3 && (
              <>
                <p className="text-sm text-slate-600 -mt-2">
                  Describe the problem you are seeing. What is happening? Why does it matter? What happens if nobody responds?
                </p>
                <Area label="What is happening?" name="what_is_happening" value={form.what_is_happening} onChange={setField} />
                <Area label="Why does this problem matter?" name="why_problem_matters" value={form.why_problem_matters} onChange={setField} />
                <Area label="Who is most affected?" name="who_is_affected" value={form.who_is_affected} onChange={setField} />
                <Area label="What may happen if no one responds?" name="if_no_response" value={form.if_no_response} onChange={setField} />
              </>
            )}

            {step === 4 && (
              <>
                <p className="text-sm text-slate-600 -mt-2">
                  What support, services, or resources are missing right now?
                </p>
                <CheckGroup
                  name="missing_needs"
                  options={MISSING_NEEDS}
                  selected={form.missing_needs}
                  onToggle={toggleArr}
                />
                <Field label="Other (optional)" name="missing_needs_other" value={form.missing_needs_other} onChange={setField} />
                <Area label="Describe the unaddressed needs in your own words" name="unaddressed_needs_text" value={form.unaddressed_needs_text} onChange={setField} rows={4} />
              </>
            )}

            {step === 5 && (
              <>
                <p className="text-sm text-slate-600 -mt-2">
                  How will your organization respond to the need?
                </p>
                <Area label="What program, service, class, workshop, outreach, or support will you provide?" name="program_description" value={form.program_description} onChange={setField} rows={4} />
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="How often will it happen?" name="program_frequency" value={form.program_frequency} onChange={setField} placeholder="e.g. 4 days per week" />
                  <Field label="Where will it happen?" name="program_location" value={form.program_location} onChange={setField} placeholder="e.g. Community Center, Elm Street" />
                  <Field label="How many people will be served?" name="people_served" value={form.people_served} onChange={setField} placeholder="e.g. 60 students per year" />
                </div>
                <Area label="What makes this response meaningful or different?" name="what_makes_meaningful" value={form.what_makes_meaningful} onChange={setField} />
              </>
            )}

            {step === 6 && (
              <>
                <p className="text-sm text-slate-600 -mt-2">
                  No strong proposal stands alone. Who will you work with?
                </p>
                <CheckGroup
                  name="partners"
                  options={PARTNER_OPTIONS}
                  selected={form.partners}
                  onToggle={toggleArr}
                />
                <Field label="Other (optional)" name="partners_other" value={form.partners_other} onChange={setField} />
                <Area label="List your partners and explain why they matter" name="partners_explanation" value={form.partners_explanation} onChange={setField} rows={4} />
              </>
            )}

            {step === 7 && (
              <>
                <p className="text-sm text-slate-600 -mt-2">
                  Every solution has a cost. What will the funding help pay for?
                </p>
                <CheckGroup
                  name="cost_categories"
                  options={COST_OPTIONS}
                  selected={form.cost_categories}
                  onToggle={toggleArr}
                />
                <Field label="Other (optional)" name="cost_categories_other" value={form.cost_categories_other} onChange={setField} />
                <Area label="Describe the major costs" name="cost_description" value={form.cost_description} onChange={setField} />
                <Area label="Add any estimated budget amounts" name="budget_estimates" value={form.budget_estimates} onChange={setField} placeholder="e.g. Staff $15,000; Supplies $3,000" />
              </>
            )}

            {step === 8 && (
              <>
                <p className="text-sm text-slate-600 -mt-2">What do you want to create?</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {PROPOSAL_TYPES.map((pt) => {
                    const active = form.proposal_type === pt.value;
                    return (
                      <button
                        key={pt.value}
                        type="button"
                        onClick={() => setField("proposal_type", pt.value)}
                        data-testid={WIZARD.proposalType(pt.value.replace(/\s+/g, "-").toLowerCase())}
                        className={`text-left rounded-xl border px-4 py-4 transition-all ${
                          active
                            ? "border-primary bg-teal-50/60 ring-2 ring-primary/30"
                            : "border-slate-200 bg-white hover:border-slate-300"
                        }`}
                      >
                        <div className="font-heading font-semibold text-slate-900">
                          {pt.value}
                        </div>
                        <div className="text-xs text-slate-600 mt-1 leading-relaxed">
                          {pt.desc}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <div className="mt-10 flex items-center justify-between gap-3 border-t border-slate-100 pt-6">
            <Button
              variant="ghost"
              onClick={back}
              disabled={step === 1 || loading}
              className="rounded-full"
              data-testid={WIZARD.backBtn}
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
            </Button>

            {step < STEPS.length ? (
              <Button
                onClick={next}
                disabled={loading}
                className="rounded-full bg-primary hover:bg-teal-900 px-6"
                data-testid={WIZARD.nextBtn}
              >
                Continue <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            ) : (
              <Button
                onClick={submit}
                disabled={loading}
                className="rounded-full bg-primary hover:bg-teal-900 px-6"
                data-testid={WIZARD.submitBtn}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Generating...
                  </>
                ) : (
                  <>
                    Generate Proposal <Sparkles className="h-4 w-4 ml-1.5" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
